import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/api';

/**
 * Weekly progression card for the player profile (Fas 8).
 *
 * Fetches the data-only recap (/api/stats/player/{id}/recap) and shows this
 * week's key metrics with deltas vs the previous week. The AI narrative is
 * fetched lazily (?ai=true) only when the user clicks the button, so the
 * profile load stays cheap.
 *
 * @param {Object} props
 * @param {string} props.steamid64 - The player's steamid64.
 * @returns {JSX.Element|null} The card, or null when there's no recent activity.
 */
export default function WeeklyRecapCard({ steamid64 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/stats/player/${steamid64}/recap`)
      .then(res => res.json())
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [steamid64]);

  const loadAi = () => {
    setAiLoading(true);
    fetch(`${API_URL}/api/stats/player/${steamid64}/recap?ai=true`)
      .then(res => res.json())
      .then(d => { setAiText(d.recap_text || 'Ingen sammanfattning kunde genereras.'); setAiLoading(false); })
      .catch(() => { setAiText('Kunde inte generera sammanfattning.'); setAiLoading(false); });
  };

  if (loading || !data || data.error) return null;
  const cur = data.current;
  const delta = data.delta;
  if (!cur || !cur.matches) return null;  // no games this week → hide the card

  const deltaClass = (v) => (v > 0 ? 'delta-up' : v < 0 ? 'delta-down' : 'delta-flat');
  const deltaStr = (v, suffix = '') => `${v > 0 ? '+' : ''}${v}${suffix}`;

  const metrics = [
    { label: 'Matcher', value: cur.matches, d: delta.matches },
    { label: 'K/D', value: cur.kd, d: delta.kd },
    { label: 'ADR', value: cur.adr, d: delta.adr },
    { label: 'HS%', value: `${cur.hs_pct}%`, d: delta.hs_pct, suffix: '%' },
    { label: 'ELO', value: deltaStr(cur.elo_delta), d: delta.elo_delta },
  ];

  return (
    <div className="stats-section">
      <h3>📈 Veckans utveckling</h3>
      <div className="stats-grid">
        {metrics.map(m => (
          <div key={m.label} className="stat-item">
            <span className="label">{m.label}</span>
            <span className="value">
              {m.value}
              {m.d !== undefined && m.d !== 0 && (
                <span className={`weekly-delta ${deltaClass(m.d)}`}>
                  {' '}{m.d > 0 ? '▲' : '▼'} {deltaStr(m.d, m.suffix || '')}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
      <div className="weekly-recap-ai">
        {aiText ? (
          <p className="weekly-recap-text">{aiText}</p>
        ) : (
          <button className="weekly-recap-btn" onClick={loadAi} disabled={aiLoading}>
            {aiLoading ? 'Genererar…' : '🤖 Generera AI-sammanfattning'}
          </button>
        )}
      </div>
    </div>
  );
}
